import { useCurrentStateAndParams } from "@uirouter/react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes, elevation } from "@similarweb/styles";
import { CompositionContainer, ECompositionTypes } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { useCategories } from "app/pages/industry-analysis/categories-context/CategoriesContext";
import { swSettings } from "app/common/services/swSettings";
import { BasicDurations } from "app/services/DurationService";
import { EPeriodSupport } from "app/UtilitiesAndConstants/UtilityFunctions/periodSupport";

import {
    NoDataContainer,
    NoDataIcon,
    NoDataTitle,
} from "../../device-distribution/single/DeviceDistributionChart/components/DeviceDistributionChartNoData/DeviceDistributionChartNoData.styles";
import { useMarketSizeData } from "../hooks/useMarketSizeData";
import { getMarketOverviewText } from "../utils";

import { Change } from "./Change";
import { Header } from "./Header";
import { Score } from "./Score";
import { UniqueVisitors } from "./UniqueVisitors";

interface IMarketSizeProps {
    showVsDate?: boolean;
}

export const MarketSize = ({ showVsDate }: IMarketSizeProps) => {
    const { translate } = useTranslate();
    const isSupported = swSettings.allowedDuration(BasicDurations.LAST_MONTH) as boolean;
    const { params } = useCurrentStateAndParams();
    const { categories } = useCategories();
    const { country } = params as { country: number; comparedDuration: string };

    const text = getMarketOverviewText();

    const primaryCategory = categories[0]?.category || null;

    const { uniqueData, absoluteUniqueValue, scoreData, changeData, periodSupport, isLoading } =
        useMarketSizeData(primaryCategory);

    const hasData = isLoading || (!!uniqueData && !!scoreData);

    return isSupported && primaryCategory ? (
        <Flex
            height="100%"
            width="100%"
            minWidth="315px"
            flexDirection="column"
            borderRadius="6px"
            background={colorsPalettes.carbon[0]}
            boxShadow={elevation.Z1}
            data-automation="market-size-audience-widget-wrapper"
        >
            <Header
                isLoading={isLoading}
                title={text.title}
                showVsDate={showVsDate}
            />
            <Flex
                justifyContent="center"
                alignItems="center"
                height="100%"
            >
                {hasData ? (
                    <CompositionContainer type={ECompositionTypes.THREE_METRICS}>
                        <UniqueVisitors
                            uniqueData={uniqueData}
                            absoluteUniqueValue={absoluteUniqueValue}
                            showGraph={periodSupport === EPeriodSupport.Yearly}
                            isLoading={isLoading}
                            title={text.uniqueVisitors}
                        />
                        <Change
                            changeData={changeData}
                            isLoading={isLoading}
                            periodSupport={periodSupport}
                            text={text.change}
                        />
                        <Score
                            isLoading={isLoading}
                            scoreData={scoreData}
                            country={country}
                            text={text.score}
                        />
                    </CompositionContainer>
                ) : (
                    <NoDataContainer>
                        <NoDataIcon iconName={"no-data-lab"} />
                        <NoDataTitle>{translate("global.nodata.notavilable")}</NoDataTitle>
                    </NoDataContainer>
                )}
            </Flex>
        </Flex>
    ) : null;
};
